import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Flex } from '@rebass/grid'
import { LuImagePlus, LuSendHorizontal } from 'react-icons/lu'
import { handleErrorResponse } from '../../../utlils/general'
import Input from '../../atoms/Input'
import Button from '../../atoms/Button'

export const ERROR_QUOTA_EXCEEDED = 'QUOTA_EXCEEDED'

const getInputHeight = (line) => {
  const lineHeight = 1.2
  const padding = 0.8 * 2
  return `${lineHeight * line + padding}rem`
}

const MessageSender = ({ onSend, hasImagePicker, onPickImage, presetMessage, imageCount, disabled, ...props }) => {
  const [message, setMessage] = useState('')
  const [inputRows, setInputRows] = useState(1)
  const uploadInputRef = useRef()

  const unableToSend = useMemo(
    () => disabled || ((!(message || '').trim()) && imageCount === 0),
    [disabled, message, imageCount],
  )

  const onSendMessage = useCallback(async () => {
    if (unableToSend) return
    try {
      await onSend((message || '').trim())
      setMessage('')
    } catch (err) {
      if (err.message === ERROR_QUOTA_EXCEEDED) {
        return
      }
      handleErrorResponse(err, () => {}, '系統有啲野出錯左！\n請等陣再試>.<')
    }
  }, [message, unableToSend, onSend])

  const onKeyDown = useCallback((e) => {
    // for mobile, enter key always open new line
    // for desktop, enter key will send message, shift + enter will open new line
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    if (!isMobile && e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault()
      return onSendMessage()
    }
    return false
  }, [onSendMessage])

  const openFilePicker = useCallback(() => {
    if (uploadInputRef.current) {
      uploadInputRef.current.click()
    }
  }, [])

  useEffect(() => {
    if (presetMessage) {
      setMessage(presetMessage)
    }
  }, [presetMessage])

  useEffect(() => {
    const lineCount = (message || '').split('\n').length
    setInputRows(Math.min(8, lineCount))
  }, [message])

  return (
    <Flex alignItems="flex-end" {...props}>
      <Flex flex={1} style={{ position: 'relative' }}>
        <Input
          type="textarea"
          placeholder="輸入訊息..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          readOnly={disabled}
          onKeyDown={onKeyDown}
          rows={inputRows}
          style={{
            fieldSizing: 'content',
            minHeight: getInputHeight(1),
            maxHeight: getInputHeight(8),
            paddingRight: '2.5rem',
          }}
        />
        {hasImagePicker && (
          <Flex style={{ position: 'absolute', right: 0 }} alignItems="center">
            <Button
              padding="0.625rem"
              variant="text"
              onClick={openFilePicker}
              disabled={disabled}
            >
              <LuImagePlus size={24} />
            </Button>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              ref={uploadInputRef}
              onChange={(e) => onPickImage(Array.from(e.target.files || []))}
              multiple
            />
          </Flex>
        )}
      </Flex>
      <Flex ml="0.25rem">
        <Button
          padding="0.625rem"
          onClick={onSendMessage}
          disabled={unableToSend}
        >
          <LuSendHorizontal size={24} />
        </Button>
      </Flex>
    </Flex>
  )
}

MessageSender.propTypes = {
  onSend: PropTypes.func,
  hasImagePicker: PropTypes.bool,
  onPickImage: PropTypes.func,
  presetMessage: PropTypes.string,
  imageCount: PropTypes.number,
  disabled: PropTypes.bool,
}

MessageSender.defaultProps = {
  onSend: () => {},
  hasImagePicker: false,
  onPickImage: () => {},
  presetMessage: '',
  imageCount: 0,
  disabled: false,
}

export default MessageSender
